import {isAnyOf} from '@reduxjs/toolkit'

import {omit} from '@shared/helpers/object'

import {
    getGroupedCardPromotions,
    getIntegratedDiscount,
    getMyOptimalDiscountPromotion,
    getPromotionStatus,
} from '@web/actions/orderSheetDiscount'
import {
    EasyCardItemType,
    PaymentsCardCodeType,
    PayMethod,
    PromotionStatus,
    PromotionCardCodeType,
    BannerTypeType,
    PromotionCardProductNameCodeType,
    PaymentsCardCode,
    APIStatus,
} from '@web/core/constants'
import {getCardCodeName} from '@web/helpers/policies'
import {
    CardState,
    setupLongInterestFreeInstallments,
    getCardEasyPaysFirstItem,
    getCommonInstallmentMinAmount,
    getCommonInstallments,
    isApplicableCardProductCodes,
} from '@web/slices/card/slice'
import makeSlice from '@web/slices/card/sliceMaker'

import {initOrderSheetByAPI} from '../../actions'
import {InterestInstallment} from '../../apis/response/interface/payMethod/billing'
import {CardEasyPayView} from '../../apis/response/interface/payMethod/cardEasyPay'
import {MerchantExceptionPolicy} from '../../constants'

const convertCardType = (cardType: string) => cardType as PaymentsCardCodeType

const convertEasyCard = (
    cardEasyPays: CardEasyPayView[],
    limitedCardList?: string[],
    interestInstallments?: InterestInstallment[],
): CardState['cardEasyPay']['items'] => {
    return cardEasyPays.map((cardEasyPay) => {
        const companyCode = convertCardType(cardEasyPay.cardCompanyCode)
        return {
            category: cardEasyPay.cardCategory,
            companyCode,
            companyId: cardEasyPay.cardCompanyId,
            companyName: `${getCardCodeName(companyCode)}카드`,
            id: cardEasyPay.cardId,
            imageUrl: cardEasyPay.cardImageUrl,
            name: cardEasyPay.cardName,
            ownerType: cardEasyPay.cardOwnerType,
            productCode: cardEasyPay.cardProductCode,
            type: cardEasyPay.cardType ?? 'CREDIT',
            maskedNumber: cardEasyPay.maskedCardNumber,
            payService: {
                payable: cardEasyPay.payServiceAvailable,
                maintenance: cardEasyPay.payServiceMaintenanceSchedule,
                supportedMerchant: true,
            },
            longTermInstallment: cardEasyPay.longTermInstallment,
            registeredDateTime: cardEasyPay.registeredDateTime,
            reward: !cardEasyPay.rewardCard
                ? null
                : {
                      rewardAccumulateRate: cardEasyPay.rewardAccumulateRate,
                      rewardPointResidualAmount: cardEasyPay.rewardPointResidualAmount,
                      minAmount: 0,
                  },
            interestFreeBenefitPromotion: {
                isAvailable: true,
                minAmount: 0,
                month: 0,
            },
            plcc: cardEasyPay.hyundaiPlccCard
                ? {
                      type: 'NAVER_HYUNDAI',
                  }
                : null,
            issuerCode: cardEasyPay.cardIssuerCode,
            baseInstallmentInfo: getCommonInstallments(convertCardType(cardEasyPay.cardCompanyCode), {
                limitedCardList: limitedCardList ?? [],
                interestInstallments: interestInstallments ?? [],
            }),
        }
    })
}

const slice = makeSlice({
    extraReducers: (builder) => {
        builder
            .addCase(initOrderSheetByAPI.fulfilled, (state, {payload}) => {
                const {
                    payMethod,
                    cardPromotionV2: cardPromotion,
                    cardPromotionV2FromMyOptimalDiscount,
                    banner,
                } = payload.data

                state.cardEasyPay.bannerItemTypes =
                    banner.bannerListsByExposureAreaType['EASY_PAY_CREDIT_CARD']?.reduce<
                        {
                            cardCompanyCode: PromotionCardCodeType
                            bannerType: BannerTypeType
                            cardProductNameCode: Maybe<PromotionCardProductNameCodeType>
                        }[]
                    >((acc, {cardCompanyCode, bannerType, cardProductNameCode}) => {
                        if (cardCompanyCode) {
                            acc.push({cardCompanyCode, bannerType, cardProductNameCode})
                        }
                        return acc
                    }, []) ?? []

                // 카드 프로모션
                if (cardPromotion) {
                    state.promotion.status = getPromotionStatus({
                        groupedCardPromotions: cardPromotion.groupedCardPromotions,
                    })
                    state.promotion.apiStatus = APIStatus.RESOLVED
                    state.promotion.skipInfo = {
                        isSuggested: false,
                        isConnectedEasyBank: false,
                    }

                    const groups = getGroupedCardPromotions({
                        groups: cardPromotion.groupedCardPromotions,
                        failed: cardPromotion.failedCardPromotions,
                    })
                    state.promotion.groups = groups
                    if (cardPromotionV2FromMyOptimalDiscount) {
                        const myOptimizedGroups = getGroupedCardPromotions({
                            groups: cardPromotionV2FromMyOptimalDiscount.groupedCardPromotions,
                            failed: cardPromotionV2FromMyOptimalDiscount.failedCardPromotions,
                        })
                        state.promotion.myOptimizedGroups = myOptimizedGroups
                    }

                    state.promotion.myOptimalDiscountGroup = getMyOptimalDiscountPromotion(groups)
                } else {
                    state.promotion.status = PromotionStatus.NO_PROMOTION
                    state.promotion.apiStatus = APIStatus.IDLE
                }

                // 카드 간편결제
                state.cardEasyPay.items = convertEasyCard(payMethod.cardEasyPays)
                state.cardEasyPay.limitedInstallmentMonth = payMethod.billingPayMethods.limitedInstallmentMonth
                state.cardEasyPay.interestFreeInstallments = setupLongInterestFreeInstallments(
                    payMethod.billingPayMethods.cardEasyPay?.interestFreeInstallments.map((info) => ({
                        ...info,
                        minAmount: getCommonInstallmentMinAmount(convertCardType(info.cardCompanyCode)),
                    })) ?? [],
                    payMethod.billingPayMethods.cardEasyPay?.interestInstallments ?? [],
                )

                const firstCard = getCardEasyPaysFirstItem<CardEasyPayView>(
                    ({payServiceAvailable}) => payServiceAvailable,
                )(payMethod.cardEasyPays)
                state.cardEasyPay.selected.id = firstCard?.cardId ?? ''
                state.cardEasyPay.selected.itemType = !firstCard ? EasyCardItemType.ADD_CARD : EasyCardItemType.CARD

                // 일반 카드결제
                state.creditCard.companies = payMethod.cardCompanies.map(({code, name}) => ({
                    name,
                    code: convertCardType(code),
                    baseInstallmentInfo: getCommonInstallments(convertCardType(code)),
                }))

                state.creditCard.limitedInstallmentMonth = payMethod.billingPayMethods.limitedInstallmentMonth
                state.creditCard.interestFreeInstallments = setupLongInterestFreeInstallments(
                    payMethod.billingPayMethods.card?.interestFreeInstallments.map((info) => ({
                        ...info,
                        minAmount: getCommonInstallmentMinAmount(convertCardType(info.cardCompanyCode)),
                    })) ?? [],
                    // @see https://oss.fin.navercorp.com/Product-Planning/Order_Plan/issues/2754#issuecomment-2145863
                    payMethod.billingPayMethods.cardEasyPay?.interestInstallments?.filter(
                        ({applicableCardProductCode}) => {
                            return isApplicableCardProductCodes(applicableCardProductCode ?? '')
                        },
                    ) ?? [],
                )

                state.creditCardBillDiscounts = payMethod.creditCardBillDiscounts.map((billDiscount) => ({
                    benefit: billDiscount.benefit,
                    cardCompanyCode: billDiscount.corporationCode as PaymentsCardCodeType,
                    discountRate: billDiscount.discountRate,
                    startDate: billDiscount.startDate,
                    endDate: billDiscount.endDate,
                    linkUrl: billDiscount.linkUrl,
                    additionDescription: billDiscount.orderAdditionDescription,
                    detailDescription: billDiscount.orderDetailDescription,
                    rank: billDiscount.rank,
                    eventType: billDiscount.eventType,
                }))
                state.partnership.containsCreditCard = payMethod.shinhanCardPartnership.containsCreditCard
                state.partnership.containsShinhanPartnershipCard =
                    payMethod.shinhanCardPartnership.containsShinhanPartnershipCard

                if (payMethod.billingPayMethods.cardEasyPay) {
                    state.cardEasyPay.billing = {
                        pgCp: payMethod.billingPayMethods.cardEasyPay.pgCp,
                        pgId: payMethod.billingPayMethods.cardEasyPay.pgId,
                        minimumAmount: payMethod.billingPayMethods.cardEasyPay.minimumAmount,
                        maximumAmount: payMethod.billingPayMethods.cardEasyPay.maximumAmount,
                    }
                }
                if (payMethod.billingPayMethods.card) {
                    state.creditCard.billing = {
                        pgCp: payMethod.billingPayMethods.card.pgCp,
                        pgId: payMethod.billingPayMethods.card.pgId,
                        sid: payMethod.billingPayMethods.card.sid,
                        minimumAmount: payMethod.billingPayMethods.card.minimumAmount,
                        maximumAmount: payMethod.billingPayMethods.card.maximumAmount,
                    }
                }

                if (payMethod.defaultPayment?.payMethodType === PayMethod.CARD) {
                    state.creditCard.selected.companyCode = payMethod.defaultPayment.cardCompanyCode
                        ? convertCardType(payMethod.defaultPayment.cardCompanyCode)
                        : null
                }
            })
            .addCase(getIntegratedDiscount.pending, (state) => {
                state.promotion.apiStatus = APIStatus.FETCHING
            })
            .addCase(getIntegratedDiscount.fulfilled, (state, {payload}) => {
                const {data, from, myOptimalDiscountDiff, shouldSelectedUpdate} = payload
                const {cardPromotionV2: cardPromotion, cardPromotionV2FromMyOptimalDiscount} = data

                if (cardPromotion) {
                    state.promotion.apiStatus = APIStatus.RESOLVED

                    const groups = getGroupedCardPromotions({
                        groups: cardPromotion.groupedCardPromotions,
                        failed: cardPromotion.failedCardPromotions,
                    })
                    const myOptimalDiscountGroup = getMyOptimalDiscountPromotion(groups)

                    // 쿠폰페이지에서 통합할인조회 호출한 경우 카드프로모션 정보 반영하지 않음
                    if (from === 'COUPON_RELOAD' && !shouldSelectedUpdate) {
                        // 나할가 정보가 변경된 경우 카드프로모션 재조회가 필요함 -> RELOAD 상태로 변경
                        if (myOptimalDiscountDiff.changed) {
                            state.promotion.status = PromotionStatus.SHOULD_RELOAD
                            state.promotion.selectedInfo = null
                            state.promotion.myOptimizedGroups = undefined
                            state.promotion.myOptimalDiscountGroup = myOptimalDiscountGroup
                        }
                    } else {
                        state.promotion.status = getPromotionStatus({
                            groupedCardPromotions: cardPromotion.groupedCardPromotions,
                        })
                        state.promotion.groups = groups
                        state.promotion.myOptimalDiscountGroup = myOptimalDiscountGroup
                        state.promotion.skipInfo = {
                            isSuggested: false,
                            isConnectedEasyBank: false,
                        }

                        if (cardPromotionV2FromMyOptimalDiscount) {
                            const myOptimizedGroups = getGroupedCardPromotions({
                                groups: cardPromotionV2FromMyOptimalDiscount.groupedCardPromotions,
                                failed: cardPromotionV2FromMyOptimalDiscount.failedCardPromotions,
                            })
                            state.promotion.myOptimizedGroups = myOptimizedGroups
                        }
                    }
                } else {
                    state.promotion.status = PromotionStatus.NO_PROMOTION
                    state.promotion.apiStatus = APIStatus.IDLE
                }
            })
            .addCase(getIntegratedDiscount.rejected, (state) => {
                state.promotion.status = PromotionStatus.UNAVAILABLE
                state.promotion.apiStatus = APIStatus.REJECTED
            })
            .addMatcher(isAnyOf(initOrderSheetByAPI.fulfilled), (state, {payload}) => {
                /**
                 * C2 자동차 일반상품 판매 관련 할부 정책
                 * @see https://wiki.fin.navercorp.com/pages/viewpage.action?pageId=171903798
                 */
                const {merchantsByMerchantNo, payMethod} = payload.data

                Object.keys(merchantsByMerchantNo).forEach((merchantNo) => {
                    const merchant = merchantsByMerchantNo[merchantNo]
                    if (merchant?.merchantExceptionPolicyTypes?.includes(MerchantExceptionPolicy.CAR_GENERAL_SALE)) {
                        /**
                         * 판매자 예외정책으로 인한 자동차 주문건인 경우
                         * limitedInstallmentPolicy 는 PARTIAL_LIMIT
                         * C2 자동차 일반상품 > 판매자예외정책
                         * 모든 무이자할부 미제공
                         * 롯데 / KB카드만 유이자할부 제공(cardEasyPay.interestInstallments 필드 참조), 그 외 모든 카드사 일시불만 제공
                         */
                        state.cardEasyPay.interestFreeInstallments = []

                        /**
                         * 국민 / 롯데 카드사의 경우 할부개월수(유이자) 제공
                         * @see https://oss.fin.navercorp.com/Product-Planning/Order_Plan/issues/2016#issuecomment-1396466
                         */
                        const limitedCardList = Object.values(omit(['KB', 'LOTTE'], PaymentsCardCode))

                        state.cardEasyPay.items = convertEasyCard(
                            payMethod.cardEasyPays,
                            limitedCardList,
                            payMethod.billingPayMethods.cardEasyPay?.interestInstallments ?? [],
                        )
                    }
                })
            })
    },
})

export default slice.reducer
